import React from "react";
import { graphql, useFragment } from "react-relay";

import { ReactComponent as MondayLogo } from "~/legacy-assets/images/logos/monday.svg";
import { promptMondayAuth } from "~/lib/auth";
import type { IntegrationConfigProps, IntegrationDetailsType } from "~/stream/StreamSettings/ActionItemIntegrations";

import type { ActionItemIntegration, BaseIntegrationDetails } from "../actionItemIntegration";
import { CreateNewExternalTicketPane } from "./MondayCreateNewExternalTicketPane";
import { ImportExistingTicketPane } from "./MondayImportExistingTicketPane";
import { MondayStreamSettingsConfigView, MondayStreamSettingsIntegrationCard } from "./MondayStreamSettings";
import { MondayActionItemIntegrationLinkWidget } from "./Widget";

import type { mondayStreamDetails$key } from "~/__generated__/mondayStreamDetails.graphql";

import styles from "~/integrations/actionItemIntegration/css/styles.module.css";

graphql`
	fragment mondaySpecificData on MondayActionItemIntegrationSpecificDataNode {
		__typename
		externalTaskId
		externalGroupName
		externalBoardId
		externalBoardName
		status

		mondayUser {
			accountSlug
		}
	}
`;

const mondayStreamDetails = graphql`
	fragment mondayStreamDetails on MondayStreamIntegrationNode {
		mondayUser {
			user {
				fullName
			}
		}
		externalGroupName
		includeAiGenerated
		externalBoardId
		externalGroupId
		externalBoardName
		assigneeColumnId
		statusColumnId
		dueDateColumnId
	}
`;

export interface MondayStreamIntegrationDetails extends BaseIntegrationDetails {
	externalBoardId: string;
	externalGroupId: string;
	externalBoardName: string;
	assigneeColumnId?: string;
	statusColumnId?: string;
	dueDateColumnId?: string;
	externalGroupName: string;
}

function useMondayStreamIntegrationDetails(integrationOrAsanaStream: IntegrationDetailsType) {
	if (!("actionItemIntegration" in integrationOrAsanaStream) || !integrationOrAsanaStream.actionItemIntegration) {
		throw new Error("Stream integration details are for Asana, not Monday"); // Should never encounter this
	}

	const streamDetails = useFragment<mondayStreamDetails$key>(
		mondayStreamDetails,
		integrationOrAsanaStream.actionItemIntegration.integrationSpecificDetails,
	);

	return {
		containerTitle: streamDetails.externalGroupName,
		createdBy: streamDetails.mondayUser.user.fullName,
		includeAiGenerated: streamDetails.includeAiGenerated,
		externalBoardId: streamDetails.externalBoardId,
		externalGroupId: streamDetails.externalGroupId,
		externalBoardName: streamDetails.externalBoardName,
		externalGroupName: streamDetails.externalGroupName,
		assigneeColumnId: streamDetails.assigneeColumnId ?? undefined,
		statusColumnId: streamDetails.statusColumnId ?? undefined,
		dueDateColumnId: streamDetails.dueDateColumnId ?? undefined,
	};
}

function getMondayConfigurationPane({
	integration,
	stream,
	onFinish,
	onCancel,
	integrationDetails,
}: IntegrationConfigProps) {
	return (
		<MondayStreamSettingsConfigView
			integration={integration}
			stream={stream}
			onFinish={onFinish}
			onCancel={onCancel}
			integrationDetails={integrationDetails as MondayStreamIntegrationDetails}
		/>
	);
}

export const monday: ActionItemIntegration = {
	key: "MONDAY",
	logo: <MondayLogo className={styles.integrationLogo} />,
	billingFeatureName: "monday_integration",
	displayName: "monday.com",
	externalActionItemName: "item",
	externalContainerType: "group",
	renderPill: MondayActionItemIntegrationLinkWidget,
	renderCreateNewExternalTicketPane: CreateNewExternalTicketPane,
	renderImportExistingExternalTicketPane: ImportExistingTicketPane,
	promptAuth: promptMondayAuth,
	streamSettingsCard: MondayStreamSettingsIntegrationCard,
	configurationPane: getMondayConfigurationPane,
	useIntegrationDetails: useMondayStreamIntegrationDetails,
};
